import { getFirestoreDB } from "@risalabs_frontend_org/auth-wrapper";
import { doc, setDoc } from "firebase/firestore";
import { stripUndefined } from "../../../medonc/pages/authorisationDetails/utils/saveReviewHelpers";
import {
  createAgenticAuthOnFileRemarkEntry,
  serializeAgenticAuthRemarksForFirestore,
} from "../../../medonc/pages/configurations/pages/agenticAuthOnFileConfiguration/utils/agenticAuthOnFileRemarks";
import {
  rowsFromAgenticAuthOnFilePayersDoc,
  toAgenticAuthOnFilePayerKey,
  type AgenticAuthOnFileRow,
} from "../../../medonc/pages/configurations/pages/agenticAuthOnFileConfiguration/utils/agenticAuthOnFileTable";
import { logError } from "../../../medonc/utils/customLogger";
import { FirestoreService } from "../firebase/firestoreService";
import { FirestoreDocumentReference } from "../firebase/references";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export class AgenticAuthOnFileConfigService {
  /**
   * Document `configurations/agentic-auth-on-file/{orgId}/payers`: each field
   * is a payer name with a map value `{ agent_mode, enabled, … }`.
   */
  static async getPayers(
    organizationId: string,
  ): Promise<AgenticAuthOnFileRow[]> {
    if (!organizationId) {
      return [];
    }
    const documentPath =
      FirestoreDocumentReference.agenticAuthOnFilePayers(organizationId);
    try {
      const data =
        await FirestoreService.getDocument<Record<string, unknown>>(
          documentPath,
        );
      return rowsFromAgenticAuthOnFilePayersDoc(data);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === "Document does not exist!"
      ) {
        return [];
      }
      logError(
        error as Error,
        `Error fetching agentic auth on file payers for org ${organizationId}`,
      );
      throw error;
    }
  }

  /**
   * Persists toggle changes for the given rows by merging into existing payer maps.
   */
  static async savePayerToggles(
    organizationId: string,
    changedRows: AgenticAuthOnFileRow[],
    updatedBy?: string,
  ): Promise<void> {
    if (!organizationId || changedRows.length === 0) {
      return;
    }
    const documentPath =
      FirestoreDocumentReference.agenticAuthOnFilePayers(organizationId);

    let existing: Record<string, unknown> = {};
    try {
      existing =
        await FirestoreService.getDocument<Record<string, unknown>>(
          documentPath,
        );
    } catch (error) {
      if (
        !(
          error instanceof Error && error.message === "Document does not exist!"
        )
      ) {
        throw error;
      }
    }

    const updates: Record<string, unknown> = {};
    const now = new Date().toISOString();

    for (const row of changedRows) {
      const prev = existing[row.id];
      const base = isPlainObject(prev) ? { ...prev } : {};
      updates[row.id] = stripUndefined({
        ...base,
        enabled: row.enabled,
        agent_mode: row.agent_mode,
        updated_at: now,
        remarks: serializeAgenticAuthRemarksForFirestore(row.remarks),
        ...(updatedBy ? { updated_by: updatedBy } : {}),
      });
    }

    const sanitized = stripUndefined(updates);

    const docRef = doc(getFirestoreDB(), documentPath);
    await setDoc(docRef, sanitized, { merge: true });
  }

  /**
   * Adds a new payer field to `…/payers` (merge). Key is derived from display name.
   */
  static async addPayer(
    organizationId: string,
    payerDisplayName: string,
    options: { enabled: boolean; agent_mode: boolean },
    remarkText: string,
    updatedBy?: string,
  ): Promise<void> {
    if (!organizationId) {
      throw new Error("Organization is required.");
    }
    const payerKey = toAgenticAuthOnFilePayerKey(payerDisplayName);
    if (!payerKey) {
      throw new Error("Payer name is required.");
    }
    const trimmedRemark = remarkText.trim();

    const documentPath =
      FirestoreDocumentReference.agenticAuthOnFilePayers(organizationId);

    let existing: Record<string, unknown> = {};
    try {
      existing =
        await FirestoreService.getDocument<Record<string, unknown>>(
          documentPath,
        );
    } catch (error) {
      if (
        !(
          error instanceof Error && error.message === "Document does not exist!"
        )
      ) {
        throw error;
      }
    }

    if (existing[payerKey] && isPlainObject(existing[payerKey])) {
      throw new Error("A payer with this name already exists.");
    }

    const now = new Date().toISOString();
    const userLabel = updatedBy ?? "—";
    const remarks = serializeAgenticAuthRemarksForFirestore([
      [createAgenticAuthOnFileRemarkEntry(userLabel, trimmedRemark)],
    ]);

    const docRef = doc(getFirestoreDB(), documentPath);
    await setDoc(
      docRef,
      stripUndefined({
        [payerKey]: {
          enabled: options.enabled,
          agent_mode: options.agent_mode,
          updated_at: now,
          remarks,
          ...(updatedBy ? { updated_by: updatedBy } : {}),
        },
      }),
      { merge: true },
    );
  }
}
